import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FilterAutocomplete from '../../components/common/FilterAutocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LockIcon from '@mui/icons-material/Lock';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import * as materialApi from '../../api/materialApi';
import * as materialGroupApi from '../../api/materialGroupApi';
import * as departmentApi from '../../api/departmentApi';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from '../../context/SnackbarContext';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { getCached, bust } from '../../utils/apiCache';

// Stable sentinel objects — referentially identical on every render so
// Autocomplete isOptionEqualToValue never causes spurious re-renders.
const NO_DEPT  = Object.freeze({ id: '', label: 'All Departments' });
const NO_GROUP = Object.freeze({ id: '', label: 'All Groups' });

export default function MaterialsTab() {
  // Current page's rows only — server-paginated, never the full table.
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Filter state
  const [filterDept, setFilterDept] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterName, setFilterName] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', materialGroupId: '', unit: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('MANAGE_MASTER_DATA');

  // Reference lists — small and bounded regardless of table size, so these
  // stay cached as before (unlike the materials list itself).
  useEffect(() => {
    (async () => {
      try {
        const [g, d] = await Promise.all([
          getCached('materialGroups:all', () => materialGroupApi.getAll()),
          getCached('departments:all', () => departmentApi.getAll()),
        ]);
        setGroups(Array.isArray(g) ? g : (g.data || []));
        setDepartments(Array.isArray(d) ? d : (d.data || []));
      } catch (e) { showSnackbar(e.message, 'error'); }
    })();
  }, [showSnackbar]);

  // ── Server-side paginated + filtered fetch ─────────────────────────────────
  // Materials are never cached client-side here — at 100k+ rows a per-filter
  // cache entry would just re-create the same localStorage-quota problem the
  // old 'materials:all' full-fetch had. Live query per page/filter instead.
  const load = useCallback(() => {
    setRefreshing(true);
    const params = { page: page + 1, limit: rowsPerPage };
    if (filterName.trim()) params.search = filterName.trim();
    if (filterGroup) params.materialGroupId = filterGroup;
    else if (filterDept) params.departmentId = filterDept;

    materialApi.getAll(params)
      .then((res) => {
        setRows(res.data || []);
        setTotal(res.total ?? (res.data ? res.data.length : 0));
      })
      .catch((e) => showSnackbar(e.message, 'error'))
      .finally(() => { setRefreshing(false); setInitialLoading(false); });
  }, [page, rowsPerPage, filterName, filterGroup, filterDept, showSnackbar]);

  // Debounced — covers both filter changes and search keystrokes with one timer.
  // The very first load fires immediately (no artificial delay before the user
  // sees any data); only subsequent filter/search changes get the 300ms debounce.
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) { isFirstLoad.current = false; load(); return; }
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Reset selection AND page when filters change
  useEffect(() => { setSelectedIds(new Set()); setPage(0); }, [filterDept, filterGroup, filterName]);

  // ── Scroll to top of the main layout container on every page / page-size change ──
  // Without this the browser clamps scrollTop to the new (shorter) DOM height when
  // page N has fewer rows than page N-1, leaving the viewport in the middle of the
  // table and the pagination controls below the fold.
  const scrollSentinelRef = useRef(null);
  useEffect(() => {
    // Scroll the named main container so the very top of this tab is visible.
    // Falls back to scrollIntoView on the sentinel element if the id lookup fails.
    const mainEl = document.getElementById('main-scroll');
    if (mainEl) {
      mainEl.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      scrollSentinelRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, [page, rowsPerPage]);

  // ── Instant client-side filtering (no API call) ───────────────────────────────
  const filteredGroups = useMemo(() =>
    filterDept ? groups.filter((g) => g.departmentId === Number(filterDept)) : groups,
    [groups, filterDept]
  );

  // ── Autocomplete options for toolbar filters ──────────────────────────────
  const deptOptions = useMemo(() => [
    NO_DEPT,
    ...departments.map((d) => ({ id: String(d.id), label: d.name })),
  ], [departments]);

  const groupOptions = useMemo(() => [
    NO_GROUP,
    ...filteredGroups.map((g) => ({
      id: String(g.id),
      label: filterDept ? g.groupCode : `${g.groupCode} — ${g.department?.name}`,
    })),
  ], [filteredGroups, filterDept]);

  const currentDept  = useMemo(() => deptOptions.find((o) => o.id === String(filterDept))  ?? NO_DEPT,  [deptOptions,  filterDept]);
  const currentGroup = useMemo(() => groupOptions.find((o) => o.id === String(filterGroup)) ?? NO_GROUP, [groupOptions, filterGroup]);

  // ── Filter helpers ────────────────────────────────────────────────────────────
  const handleDeptChange = (deptId) => { setFilterDept(deptId); setFilterGroup(''); };
  const handleGroupChange = (groupId) => {
    setFilterGroup(groupId);
    if (groupId && !filterDept) {
      const group = groups.find((g) => String(g.id) === groupId);
      if (group?.departmentId) setFilterDept(String(group.departmentId));
    }
  };
  const clearFilters = () => { setFilterDept(''); setFilterGroup(''); setFilterName(''); };
  const hasFilters = filterDept || filterGroup || filterName;

  // ── Selection helpers — "select all" is page-scoped (matches PriceManagement's
  // existing bulk-select behavior); cross-page selection still works if the user
  // manually checks rows on more than one page before deleting.
  const toggleRow = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => (rows.length > 0 && rows.every((m) => selectedIds.has(m.id)))
    ? setSelectedIds(new Set())
    : setSelectedIds(new Set(rows.map((m) => m.id)));
  const allSelected = rows.length > 0 && rows.every((m) => selectedIds.has(m.id));
  const someSelected = rows.some((m) => selectedIds.has(m.id)) && !allSelected;

  // ── Bulk delete ───────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      const res = await materialApi.batchRemove([...selectedIds]);
      showSnackbar(res.message || `${selectedIds.size} material(s) deleted.`, 'success');
      setSelectedIds(new Set());
      bust('stats'); // Dashboard's Materials count card reads from this cache
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', materialGroupId: filterGroup || '', unit: '' });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditItem(row);
    setForm({ name: row.name, materialGroupId: row.materialGroupId, unit: row.unit });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.materialGroupId) return;
    if (!editItem && !form.unit.trim()) return;
    setSaving(true);
    try {
      if (editItem) {
        await materialApi.update(editItem.id, { name: form.name, materialGroupId: form.materialGroupId });
        showSnackbar('Material updated', 'success');
      } else {
        await materialApi.create(form);
        showSnackbar('Material created', 'success');
      }
      setDialogOpen(false);
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await materialApi.remove(deleteTarget.id);
      showSnackbar(`"${deleteTarget.name}" deleted along with all associated pricing data.`, 'success');
      setDeleteTarget(null);
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  return (
    <Box ref={scrollSentinelRef}>
      {/* Toolbar: filters + add button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterListIcon color="action" fontSize="small" />
          <FilterAutocomplete
            label="Department"
            size="small"
            sx={{ minWidth: 180 }}
            options={deptOptions}
            value={currentDept}
            onChange={(_, opt) => handleDeptChange(opt?.id || '')}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
          <FilterAutocomplete
            label="Group Code"
            size="small"
            sx={{ minWidth: 200 }}
            options={groupOptions}
            value={currentGroup}
            onChange={(_, opt) => handleGroupChange(opt?.id || '')}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
          <TextField
            size="small"
            placeholder="Search by name…"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /></InputAdornment> }}
            inputProps={{ autoComplete: 'off' }}
            sx={{ minWidth: 190 }}
          />
          {hasFilters && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>Clear</Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {total} material(s)
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Material
          </Button>
        )}
      </Box>

      {!canManage && !canDelete && (
        <Alert severity="info" sx={{ mb: 2 }}>You have read-only access to materials.</Alert>
      )}

      {/* Bulk action toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} material(s) selected</Typography>
          <Button variant="contained" color="error" size="small" startIcon={<DeleteSweepIcon />} onClick={() => setBulkDeleteOpen(true)}>
            Delete Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>Clear</Button>
        </Box>
      )}

      {/* Thin progress bar — visible on initial load AND every subsequent
          filter/page fetch, since the list is now a live server query. */}
      {refreshing && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {/* Table */}
      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F5F5' }}>
              {canDelete && (
                <TableCell padding="checkbox">
                  <Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={toggleAll} />
                </TableCell>
              )}
              <TableCell sx={{ width: 40 }}>#</TableCell>
              <TableCell>Material Name</TableCell>
              <TableCell>Group Code</TableCell>
              <TableCell>Department</TableCell>
              <TableCell sx={{ width: 90 }}>
                Unit&nbsp;
                <Tooltip title="Unit is fixed after creation">
                  <LockIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: '#bbb' }} />
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: 80 }}>Prices</TableCell>
              <TableCell sx={{ width: 80 }}>History</TableCell>
              {(canManage || canDelete) && (
                <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {initialLoading ? (
              // Skeleton rows — shown only on first visit, before the first
              // response has ever come back.
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {canDelete && <TableCell padding="checkbox"><Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} /></TableCell>}
                  <TableCell><Skeleton width={24} /></TableCell>
                  <TableCell><Skeleton width={`${50 + (i % 4) * 12}%`} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={72} height={22} /></TableCell>
                  <TableCell><Skeleton width="55%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={44} height={22} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={32} height={22} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={32} height={22} /></TableCell>
                  {(canManage || canDelete) && <TableCell />}
                </TableRow>
              ))
            ) : (
              <>
                {rows.map((m, i) => { const globalIdx = page * rowsPerPage + i; return (
                  <TableRow key={m.id} hover selected={selectedIds.has(m.id)} sx={{ bgcolor: selectedIds.has(m.id) ? '#FFF8E1 !important' : undefined }}>
                    {canDelete && (
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selectedIds.has(m.id)} onChange={() => toggleRow(m.id)} />
                      </TableCell>
                    )}
                    <TableCell>{globalIdx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{m.name}</TableCell>
                    <TableCell>
                      <Chip label={m.materialGroup?.groupCode} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ color: '#757575' }}>{m.materialGroup?.department?.name}</TableCell>
                    <TableCell>
                      <Chip label={m.unit} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={m._count?.prices || 0}
                        size="small"
                        color={m._count?.prices > 0 ? 'success' : 'default'}
                        variant={m._count?.prices > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={m._count?.priceHistory || 0} size="small" variant="outlined" />
                    </TableCell>
                    {(canManage || canDelete) && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canManage && (
                          <Tooltip title="Edit material">
                            <IconButton size="small" onClick={() => openEdit(m)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete material (cascades prices)">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(m)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ); })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canDelete ? 9 : (canManage ? 8 : 7)} align="center" sx={{ py: 4, color: '#9e9e9e' }}>
                      {hasFilters ? 'No materials match the selected filters.' : 'No materials found. Add one above.'}
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
          {!initialLoading && total > 0 && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={canDelete ? 9 : (canManage ? 8 : 7)}
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[50, 100, 200]}
                  labelRowsPerPage="Rows:"
                  sx={{ borderBottom: 0 }}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Material' : 'New Material'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            autoFocus
            label="Material Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>Material Group</InputLabel>
            <Select
              value={form.materialGroupId}
              label="Material Group"
              onChange={(e) => setForm({ ...form, materialGroupId: e.target.value })}
            >
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.groupCode} — {g.department?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!editItem ? (
            <TextField
              label="Unit (e.g. MTR, NOS, KG)"
              fullWidth
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              helperText="Unit cannot be changed after creation"
            />
          ) : (
            <TextField
              label="Unit"
              fullWidth
              value={form.unit}
              disabled
              helperText={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon sx={{ fontSize: 12 }} />
                  Unit is fixed after creation and cannot be changed
                </Box>
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!form.name.trim() || !form.materialGroupId || (!editItem && !form.unit.trim()) || saving}
          >
            {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} Material(s)`}
        message={`Delete ${selectedIds.size} selected material(s) and ALL associated prices, price history, and estimation references?\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Material"
        message={`Delete "${deleteTarget?.name}"?\n\nThis will also permanently remove:\n• ${deleteTarget?._count?.prices || 0} price record(s)\n• ${deleteTarget?._count?.priceHistory || 0} price history entry/entries\n• Any estimation line items referencing this material.\n\nThis action cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
